import React, { useEffect, useState } from 'react';
import { Box, useMediaQuery, Theme } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  ResendEmailBox,
  ResendText,
  Subtitle,
  ResendButton,
} from './EmailVerification.styles';
import { Button, Flex, Text } from '@components';
import { useAuth } from '@hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useLazyUserQuery,
  useUpdateUserMarketPreferencesMutation,
  useUserQuery,
} from '@features/api/userSlice';
import { formatTime } from '@utils/functions/stringManipulation';

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { from, hasAgreedToMarketingCommunications } = state ?? {};

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm')
  );
  const { sendEmailVerificationRequest, logout } = useAuth();
  const [fetchUser] = useLazyUserQuery();
  const { data: user } = useUserQuery(undefined, {
    refetchOnFocus: true,
  });

  const [updateUserMarketPreferences] =
    useUpdateUserMarketPreferencesMutation();

  const [cooldown, setCooldown] = useState<number>(120);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.emailVerified) {
      if (from === '/signup' && hasAgreedToMarketingCommunications) {
        updateUserMarketPreferences({
          emailAlerts: true,
        });
      }
    }
  }, [
    from,
    navigate,
    updateUserMarketPreferences,
    user,
    hasAgreedToMarketingCommunications,
  ]);

  useEffect(() => {
    const interval =
      cooldown > 0 &&
      setInterval(() => {
        setCooldown((count) => count - 1);
      }, 1000);

    return () => clearInterval(interval as unknown as number);
  }, [cooldown]);

  const splittedEmail = user?.email.split('@')[0].split('');
  const convertedEmail = `${splittedEmail
    ?.map((s, i) => {
      if (splittedEmail?.length - 1 > i && i > 0) {
        return '*';
      }
      return s;
    })
    .join('')}@${user?.email?.split('@')[1]}`;

  const onSubmit = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    try {
      const user = await fetchUser().unwrap();
      if (!user) {
        onGoToLogin();
      }

      if (!user.emailVerified) {
        toast(<FormattedMessage id={'emailVerify.emailNotVerifiedYet'} />, {
          type: 'error',
        });
      } else {
        onGoToLogin();
      }
    } catch (e) {
      navigate('/login');
      console.error(e);
    }
  };

  const onResendButtonClick = async () => {
    try {
      await sendEmailVerificationRequest();
      toast(<FormattedMessage id='emailVerify.successResendEmail' />, {
        type: 'success',
      });
      setCooldown(120);
    } catch (e) {
      console.error(e);
    }
  };

  const onGoToLogin = async () => {
    try {
      if (user) {
        await logout();
      }
      navigate('/login');
    } catch (e) {
      console.error(e);
    }
  };

  if (!user) return <></>;
  return (
    <Flex
      data-testid='email-confirmation-container'
      flex={1}
      justifyContent='center'
      flexDirection='column'
    >
      <Flex
        flex={1}
        justifyContent='center'
        flexDirection='column'
        sx={{
          maxWidth: 550,
          width: '100%',
          flex: 1,
          justifyContent: 'center',
          alignSelf: 'center',
        }}
      >
        <Box mt={16}>
          <Text variant='h4' fontWeight='fontWeightBold'>
            <FormattedMessage id='emailVerify.checkYourEmail' />
          </Text>
          <Subtitle variant='h5' fontWeight='fontWeightRegular'>
            <FormattedMessage
              id='emailVerify.sendVerificationMessage'
              values={{
                email: convertedEmail,
              }}
            />
          </Subtitle>
          <ResendEmailBox>
            <Text variant='h6' fontWeight='fontWeightSemiBold'>
              <FormattedMessage
                id='emailVerify.recieveCode'
                values={{
                  resend: (
                    <ResendButton
                      data-testid='resend-button'
                      disableRipple
                      disableTouchRipple
                      disableFocusRipple
                      disabled={cooldown > 0}
                      onClick={onResendButtonClick}
                    >
                      <ResendText variant='h6' fontWeight='fontWeightSemiBold'>
                        {cooldown ? (
                          formatTime(cooldown)
                        ) : (
                          <FormattedMessage id='misc.resend' />
                        )}
                      </ResendText>
                    </ResendButton>
                  ),
                }}
              />
            </Text>
          </ResendEmailBox>
          <Flex flexDirection='column' gap={12} my={16}>
            <Button
              variant='primary'
              color='primary'
              data-testid='verify-button'
              fullWidth
              size='large'
              onClick={onSubmit}
            >
              <FormattedMessage id='emailVerify.iHaveVerifiedEmail' />
            </Button>
            <Subtitle variant='bodyBig' fontWeight='fontWeightMedium'>
              <FormattedMessage id='emailVerify.onceVerified' />
            </Subtitle>
          </Flex>
        </Box>
      </Flex>
      <Flex marginLeft={-16}>
        <Button
          variant='tertiary'
          color='primary'
          data-testid='not-you-button'
          fullWidth={isMobile}
          onClick={onGoToLogin}
        >
          <FormattedMessage id='emailVerify.notYouButton' />
        </Button>
      </Flex>
    </Flex>
  );
};

export default EmailVerification;
